<template>
  <div>
    <b-modal
      ref="new-credit-note"
      id="new-credit-note"
      hide-footer
      centered
      size="sm"
      title="New Credit Note"
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="12">
          <b-form-group
            id="amount-label"
            label="Credit Note Amount"
            label-for="amount"
            class="mb-2"
          >
            <b-form-input
              id="amount-input"
              type="number"
              :disabled="$loading"
              placeholder="Enter credit note amount"
              v-model="creditNoteAmount"
            />
          </b-form-group>
          <b-form-group
            id="datepicker-credit-note-label"
            label="Valid till"
            label-for="datepicker"
            class="creditnote-datepicker"
          >
            <b-input-group>
              <template v-slot:append>
                <b-input-group-text
                  ><font-awesome-icon :icon="['fa', 'calendar-alt']"
                /></b-input-group-text>
              </template>
              <datepicker
                id="datepicker-credit-note-input"
                :disabled-dates="disabledDates"
                placeholder="Select Date"
                input-class="form-control w-100"
                v-model="date"
              />
            </b-input-group>
          </b-form-group>
          <b-form-group
            id="remark-note-label"
            label="Remark*"
            label-for="remark"
            class="mb-2"
          >
            <b-form-textarea
              id="remark-note-input"
              placeholder="Add remark..."
              rows="3"
              style="resize: none"
              :disabled="$loading"
              v-model="creditNoteRemark"
            />
          </b-form-group>

          <div class="d-flex mt-4">
            <b-button
              block
              class="mr-1"
              variant="outline-primary"
              type="button"
              :disabled="$loading"
              id="cancel-new-note"
              @click="
                $refs['new-credit-note'].hide(), $emit('open-issue-credit')
              "
            >
              Cancel
            </b-button>
            <b-button
              block
              variant="primary"
              :disabled="checkContBtnStatus || $loading"
              @click="makePayment"
              id="continue-new-note"
            >
              <span v-if="!$loading">Continue</span>
              <b-spinner small v-else />
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      ref="store-credit"
      id="store-credit"
      hide-footer
      centered
      size="xl"
      :title="debitNoteTitle"
      no-close-on-backdrop
      @hide="closeModal"
    >
      <b-row class="negative-margin">
        <b-col md="12" v-if="creditNotes.length > 0" class="p-0">
          <ul class="batch-header py-2 credit-list list-inline fs-11 mb-0">
            <li>
              <span v-if="refundCredit"> #</span>
              <div v-else class="custom-check pt-0">
                <input
                  type="checkbox"
                  value="select_all"
                  id="select_all"
                  class="pt-0"
                  @click="selectAllNotes"
                />
                <label for="select_all" class="mb-0"></label>
              </div>
            </li>
            <li>Created on</li>
            <li>Valid Till</li>
            <li>Credit Note Number</li>
            <li>Type</li>
            <li>Remark / Reference Invoice Number</li>
            <li>Value</li>
            <li>Redeemed</li>
            <li>Balance</li>
          </ul>
          <div style="max-height: 320px;" class="overflow-auto custom-scroll">
            <ul
              v-for="(item, index) in creditNotes"
              :key="item.voucher_id"
              class="credit-list list-inline py-3 fs-12 mb-0"
            >
              <li>
                <span v-if="refundCredit == true" v-text="index + 1"></span>
                <div v-else class="custom-check pt-0">
                  <input
                    type="checkbox"
                    :value="item.doc_no"
                    :id="item.doc_no"
                    class="pt-0"
                    @click="getCreditNote(item.doc_no, $event)"
                  />
                  <label :for="item.doc_no" />
                </div>
              </li>
              <li v-text="$moment(item.date_of_issue).format('Do MMM YYYY')" />
              <li v-text="$moment(item.valid_till).format('Do MMM YYYY')" />
              <li v-text="item.doc_no" />
              <li v-text="formatText(item.type)" />
              <li v-text="formatText(item.remark)" />
              <li v-text="currencySymbol + parseFloat(item.value).toFixed(2)" />
              <li
                :class="{ 'text-muted': item.redeemed == 0 }"
                v-text="currencySymbol + parseFloat(item.redeemed).toFixed(2)"
              />
              <li
                v-text="currencySymbol + parseFloat(item.balance).toFixed(2)"
              />
            </ul>
          </div>
        </b-col>
        <b-col
          md="12"
          v-else
          style="min-height: 150px"
          class="d-flex align-items-center justify-content-center"
        >
          <p class="text-muted">No credit note found!</p>
        </b-col>
      </b-row>
      <div
        class="mt-4 d-flex"
        style="justify-content: flex-end"
        v-if="refundCredit == true"
      >
        <div class="w-30 d-flex">
          <b-button
            class="mr-1"
            block
            variant="outline-primary"
            @click="toggleRefundCredit"
            id="refund-credit"
            :disabled="creditNotes.length == 0 || $loading"
          >
            Refund Credit
          </b-button>
          <b-button
            class="ml-2"
            block
            variant="primary"
            @click="newCreditNote"
            :disabled="$loading"
            id="new-credit-note"
          >
            New Credit Note
          </b-button>
        </div>
      </div>
      <div class="mt-4 d-flex" style="justify-content: flex-end" v-else>
        <div class="d-flex w-30">
          <b-button
            class="mr-1"
            block
            variant="outline-primary"
            @click="toggleRefundCredit()"
            :disabled="$loading"
            id="cancel-new-credit-note"
          >
            Cancel
          </b-button>
          <b-button
            class="ml-2"
            block
            variant="primary"
            @click="processRefund"
            :disabled="enableConfirmReturnBtn || $loading"
            id="process-refund-credit-note"
          >
            <span
              v-if="!$loading"
              v-text="'Refund ' + currencySymbol + refundTotal"
            >
            </span>
            <b-spinner small v-else />
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import Datepicker from "vuejs-datepicker";

export default {
  name: "adhoc-credit-note",
  components: { Datepicker },
  props: ["open", "customerDetails"],
  computed: {
    ...mapState({
      state: (state) => state,
    }),
    getDataFromState() {
      return this.state.billing.billing.customer.data;
    },
    debitNoteTitle() {
      if (this.refundCredit == true) {
        return "Store Credit Notes";
      } else {
        return "Select Credit Notes for Refund (" + this.totalCreditNotes + ")";
      }
    },
    totalCreditNotes() {
      let temp = 0;
      this.creditNoteNumbers.forEach((item) => {
        if (item.isChecked) temp += 1;
      });
      return temp;
    },
    checkContBtnStatus() {
      let temp = true;
      if (
        this.creditNoteRemark != "" &&
        this.date != "" &&
        this.creditNoteAmount != ""
      )
        temp = false;
      return temp;
    },
    refundTotal() {
      let temp = 0.0;

      this.creditNoteNumbers.forEach((item) => {
        if (item.isChecked) temp += parseFloat(item.amount);
      });
      return parseFloat(temp).toFixed(2);
    },
    enableConfirmReturnBtn() {
      let temp = true;
      let anyFalse = _.find(this.creditNoteNumbers, {
        isChecked: true,
      });
      if (anyFalse != undefined) temp = false;
      return temp;
    },
  },
  watch: {
    open() {
      if (this.open) {
        this.creditNoteList();
        this.refundCredit = true;
      } else this.$refs["store-credit"].hide();
    },
  },
  data() {
    return {
      date: "",
      creditNoteRemark: "",
      creditNoteAmount: "",
      creditNotes: [
        // {
        //   created: "19 Jan 2020",
        //   valid: "31 Dec 2020",
        //   number: "SC202019010002",
        //   type: "Return",
        //   remark: "INV0202019010046",
        //   value: "1500",
        //   redeemed: "500",
        //   balance: "1000",
        // },
        // {
        //   created: "19 Jan 2020",
        //   valid: "31 Dec 2020",
        //   number: "SC202019010003",
        //   type: "Ad-hoc",
        //   remark: "Remark for ad-hoc credit note creation",
        //   value: "100",
        //   redeemed: "0",
        //   balance: "1000",
        // },
        // {
        //   created: "19 Jan 2020",
        //   valid: "31 Dec 2020",
        //   number: "SC202019010004",
        //   type: "Ad-hoc",
        //   remark: "Remark for ad-hoc credit note creation",
        //   value: "100",
        //   redeemed: "0",
        //   balance: "1000",
        // },
        // {
        //   created: "19 Jan 2020",
        //   valid: "31 Dec 2020",
        //   number: "SC202019010005",
        //   type: "Return",
        //   remark: "INV0202019010046",
        //   value: "2000",
        //   redeemed: "1200",
        //   balance: "1000",
        // },
      ],
      creditNoteNumbers: [],
      refundCredit: true,
      disabledDates: {
        to: new Date(Date.now() - 8640000),
      },
    };
  },
  methods: {
    toggleRefundCredit() {
      this.creditNoteNumbers.forEach((item) => {
        item.isChecked = false;
      });
      if (this.refundCredit == false) {
        this.refundCredit = true;
      } else {
        this.refundCredit = false;
      }
    },
    closeModal() {
      this.$emit("close-issue-credit");
    },
    makePayment(trans) {
      this.$loading = true;
      let postData = {
        amount: this.refundCredit ? this.creditNoteAmount : this.refundTotal,
        total: this.refundCredit ? this.creditNoteAmount : this.refundTotal,
        type: this.refundCredit ? "adhoc_credit_note" : "refund_credit_note",
        // debit_trans: JSON.stringify(debit_trans),
        user_id: this.customerDetails.data.c_id,
        mobile: this.customerDetails.data.mobile,
        // remark: this.creditNoteRemark,
        // valid_till: this.date,
        url: "/process-to-payment",
      };
      if (!this.refundCredit) postData.debit_trans = JSON.stringify(trans);
      else {
        postData.valid_till = this.date;
        postData.remark = this.creditNoteRemark;
      }
      let data = Object.assign(postData, this.essentialsData);
      this.$store
        .dispatch("billing/checkOut", data)
        .then((res) => {
          if (res.data.status == "proceed_to_payment") {
            this.$store.commit("billing/tagCustomerDetails", res.data.customer);
            this.$router.push("/checkout");
          } else if (res.data.status == "fail") {
            let alertData = {
              message: res.data.message,
              type: "danger",
            };
            this.$showAlert = true;
            this.$store.commit("notifications/showAlert", alertData);
          }
          this.$loading = false;
        })
        .catch(() => {
          this.$loading = false;
        });
    },
    newCreditNote() {
      this.$refs["new-credit-note"].show();
      this.$refs["store-credit"].hide();
      this.creditNoteAmount = "";
      this.creditNoteRemark = "";
      this.date = "";
    },
    creditNoteList() {
      this.$loading = true;
      let postData = {
        user_id: this.customerDetails.data.c_id,
        url: "/account-sale/credit-note-list",
        trans_from: this.state.system.trans_from,
      };
      let data = Object.assign(postData, this.essentialsData);
      delete data.customer_group_code;
      axios
        .post(data.url, data)
        .then((res) => {
          this.$loading = false;
          this.creditNoteNumbers = [];
          if (res.data.status == "success") {
            this.creditNotes = res.data.data;
            if (this.creditNotes.length > 0) {
              this.creditNotes.forEach((item) => {
                this.creditNoteNumbers.push({
                  isChecked: false,
                  amount: item.balance,
                  doc_no: item.doc_no,
                  voucher_id: item.voucher_id,
                });
              });
            }
            this.$refs["store-credit"].show();
          } else if (res.data.status == "fail") {
            let alertData = {
              message: res.data.message,
              type: "danger",
            };
            this.$showAlert = true;
            this.$store.commit("notifications/showAlert", alertData);
          }
        })
        .catch(() => {
          this.$loading = false;
        });
    },
    selectAllNotes(e) {
      this.creditNoteNumbers.forEach((item) => {
        document.getElementById(item.doc_no).checked = e.srcElement.checked;
        item.isChecked = e.srcElement.checked;
      });
    },
    getCreditNote(doc_no, e) {
      let find = _.find(this.creditNoteNumbers, {
        doc_no: doc_no,
      });
      find.isChecked = e.srcElement.checked;

      let anyFalse = _.find(this.creditNoteNumbers, {
        isChecked: false,
      });
      if (anyFalse == undefined)
        document.getElementById("select_all").checked = true;
      else document.getElementById("select_all").checked = false;
    },
    processRefund() {
      let selectedNotes = [];
      this.creditNoteNumbers.forEach((item) => {
        if (item.isChecked)
          selectedNotes.push({
            amount: item.amount,
            voucher_id: item.voucher_id,
            doc_no: item.doc_no,
          });
      });
      this.makePayment(selectedNotes);
    },
  },
};
</script>

<style scoped lang="scss">
.negative-margin {
  margin-left: -23px;
  margin-right: -23px;
}
.custom-scroll {
  @include custom-scrollbar();
}
.blue-btn {
  background: $blue-color;
}
.btn-success.disabled,
.btn-success:disabled {
  background: #808080;
  border-color: #808080;
}
.batch-header {
  background: $dark-card-title-header;
}

.credit-list {
  &.list-inline {
    li {
      &:nth-child(1) {
        width: 5%;
        text-align: center;
      }
      &:nth-child(2) {
        width: 9%;
      }
      &:nth-child(3) {
        width: 9%;
      }
      &:nth-child(4) {
        width: 14%;
      }
      &:nth-child(5) {
        width: 8%;
      }
      &:nth-child(6) {
        width: 30%;
      }
      &:nth-child(7) {
        width: 8%;
      }
      &:nth-child(8) {
        width: 8%;
      }
      &:nth-child(8) {
        width: 8%;
      }
      &:nth-child(8) {
        width: 8%;
      }
    }
  }
}

.list-inline {
  color: #606060;
  &.hidden {
    opacity: 0.5;
  }
  li {
    &:nth-child(1) {
      width: 7%;
      text-align: center;
    }
    &:nth-child(2) {
      width: 14%;
    }
    &:nth-child(3) {
      width: 18%;
    }
    &:nth-child(4) {
      width: 18%;
    }
    &:nth-child(5) {
      width: 9%;
    }
    &:nth-child(6) {
      width: 9%;
    }
    &:nth-child(7) {
      width: 10%;
    }
    &:nth-child(8) {
      width: 15%;
    }
    .icon {
      cursor: pointer;
    }
    .icon {
      &.collapsed {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
